<template>
    <BaseCard padding="none" class="flex flex-col w-full mt-5">
        <div class="flex flex-col gap-6 md:p-10 p-5 justify-start">
            <div class="flex flex-col gap-10">
                <div class="flex flex-col justify-center items-start gap-6 h-auto w-full">
                    <h2 class="display-serif capitalize">{{ feedbackData?.feedback_question.question }}</h2>
                    <!-- Widget Row -->
                    <div class="flex flex-row justify-center items-stretch gap-10">
                        <!-- Stars -->
                        <div class="flex flex-col gap-2.5 justify-stretch items-start">
                            <div class="body-small text-black">{{ t("feedback.report.overallRating") }}</div>
                            <div
                                v-if="feedbackData?.feedback_summary.average_rating"
                                class="flex flex-row gap-1 relative"
                                role="img"
                                :aria-label="
                                    t('feedback.report.ratingAriaLabel', {
                                        question: feedbackData.feedback_question.question,
                                        rating: feedbackData.feedback_summary.average_rating,
                                    })
                                ">
                                <i v-for="n in 5" :key="n" class="bi bi-star-fill text-border px-1" aria-hidden="true"></i>
                                <div class="flex flex-row gap-1 overflow-x-hidden absolute" :style="{ width: `${feedbackData?.feedback_summary.average_rating * 20}%` }">
                                    <i v-for="n in 5" :key="n" class="bi bi-star-fill text-ink px-1" aria-hidden="true"></i>
                                </div>
                            </div>
                            <div v-else class="body-small text-black pl-1">{{ t("feedback.report.noRatings") }}</div>
                        </div>
                        <!-- Feedback Count -->
                        <div class="flex flex-col justify-center items-start">
                            <div class="body-small text-black">{{ t("feedback.report.participantsLabel") }}</div>
                            <div class="font-serif text-[48px]/none text-ink">
                                {{ formatNumber(feedbackData?.feedback_response_count) }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col gap-1">
                    <div class="body-small-semibold text-black">{{ t("feedback.report.overviewHeading") }}</div>
                    <div class="body-small text-black">{{ feedbackData?.feedback_summary.theme_summary }}</div>
                </div>
                <!-- Next row growth and strengths -->
                <div class="flex flex-row justify-around items-start gap-5">
                    <div class="flex flex-col w-full gap-1">
                        <div class="flex flex-row gap-2 justify-start items-center">
                            <i class="bi bi-trophy text-base" aria-hidden="true"></i>
                            <div class="body-small-semibold text-black">{{ t("feedback.report.topStrengthsHeading") }}</div>
                        </div>
                        <template v-if="topStrengths.length > 0">
                            <div class="px-3 body-small text-black">
                                <ul class="pl-2 m-0">
                                    <li v-for="(strength, index) in topStrengths" :key="index" class="m-0!">{{ strength }}</li>
                                </ul>
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-3 pt-2 pl-6 body-small text-black">{{ t("feedback.report.notEnoughData") }}</div>
                        </template>
                    </div>
                    <div class="flex flex-col w-full">
                        <div class="flex flex-row gap-2 justify-start items-center">
                            <i class="bi bi-signpost-split text-base" aria-hidden="true"></i>
                            <div class="body-small-semibold text-black">{{ t("feedback.report.growthAreasHeading") }}</div>
                        </div>
                        <template v-if="growthAreas.length > 0">
                            <div class="px-3 body-small text-black">
                                <ul class="p-2 m-0">
                                    <li v-for="(growth, index) in growthAreas" :key="index" class="m-0!">{{ growth }}</li>
                                </ul>
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-3 pt-2 pl-6 body-small text-black">{{ t("feedback.report.notEnoughData") }}</div>
                        </template>
                    </div>
                </div>
                <div class="flex flex-row justify-around items-start gap-5">
                    <div class="flex flex-col w-full">
                        <div class="flex flex-row gap-2 justify-start items-center">
                            <i class="bi bi-question-circle text-base" aria-hidden="true"></i>
                            <div class="body-small-semibold text-black">{{ t("feedback.report.mixedSignalsHeading") }}</div>
                        </div>
                        <template v-if="mixedSignals.length > 0">
                            <div class="px-3 body-small text-black">
                                <ul class="pl-2 m-0">
                                    <li v-for="(signal, index) in mixedSignals" :key="index" class="m-0!">{{ signal }}</li>
                                </ul>
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-3 pt-2 pl-6 body-small text-black">{{ t("feedback.report.notEnoughData") }}</div>
                        </template>
                    </div>
                    <div class="flex flex-col w-full">
                        <div class="flex flex-row gap-2 justify-start items-center">
                            <i class="bi bi-magic text-base" aria-hidden="true"></i>
                            <div class="body-small-semibold text-black">{{ t("feedback.report.examplesHeading") }}</div>
                        </div>
                        <template v-if="feedbackData?.feedback_summary.illustrative_examples.length > 0">
                            <div class="px-3 body-small text-black">
                                <ul class="p-2 m-0">
                                    <li v-for="(example, index) in feedbackData?.feedback_summary.illustrative_examples" :key="index" class="m-0!">{{ example }}</li>
                                </ul>
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-3 pt-2 pl-6 body-small">{{ t("feedback.report.notEnoughData") }}</div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseCard from "~vue/components/BaseCard.vue";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import type { FeedbackData } from "./types";

type FeedbackSummaryReportProps = {
    feedbackData: FeedbackData;
};

const { feedbackData } = defineProps<FeedbackSummaryReportProps>();
const { t } = useI18n();

const topStrengths = computed(() => feedbackData?.feedback_summary.top_strengths ?? []);
const growthAreas = computed(() => feedbackData?.feedback_summary.growth_areas ?? []);
const mixedSignals = computed(() => feedbackData?.feedback_summary.mixed_signals ?? []);
</script>
